import {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	type ReactElement,
} from "react";
import { useNavigate } from "@tanstack/react-router";
import DOMPurify from "dompurify";
import { marked, Renderer } from "marked";
import type { Tokens } from "marked";

// CONTEXT: Synthesis docs contain links like https://clearvoid.ai/doc/{id}
// We intercept these to use SPA navigation instead of full page loads.
const INTERNAL_DOC_PATTERN =
	/^https?:\/\/(www\.)?clearvoid\.(ai|com)\/doc\/([a-f0-9-]+)/;

const renderer = new Renderer();
renderer.link = function ({ href, title, tokens }: Tokens.Link) {
	const text = this.parser.parseInline(tokens);
	const titleAttr = title ? ` title="${title}"` : "";
	return `<a href="${href}" target="_blank" rel="noreferrer"${titleAttr}>${text}</a>`;
};

marked.use({ renderer, gfm: true });

interface MarkdownRendererProps {
	markdown: string;
	className?: string;
	highlight?: string;
	chunkIndex?: number;
}

function escapeRegExp(s: string): string {
	return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/** Must match server-side chunkContent in upstashSearch.ts */
const MAX_CONTENT_CHARS = 3800;

function chunkContent(text: string, maxChars: number): string[] {
	if (text.length <= maxChars) return [text];

	const chunks: string[] = [];
	let remaining = text;

	while (remaining.length > 0) {
		if (remaining.length <= maxChars) {
			chunks.push(remaining);
			break;
		}

		let splitAt = remaining.lastIndexOf("\n\n", maxChars);
		if (splitAt <= 0) {
			splitAt = remaining.lastIndexOf("\n", maxChars);
		}
		if (splitAt <= 0) {
			splitAt = maxChars;
		}

		chunks.push(remaining.slice(0, splitAt));
		remaining = remaining.slice(splitAt).trimStart();
	}

	return chunks;
}

export function MarkdownRenderer({
	markdown,
	className = "",
	highlight,
	chunkIndex,
}: MarkdownRendererProps): ReactElement {
	const containerRef = useRef<HTMLDivElement>(null);
	const navigate = useNavigate();

	// Intercept clicks on internal doc links for SPA navigation
	const handleClick = useCallback(
		(e: React.MouseEvent<HTMLDivElement>) => {
			const target = (e.target as HTMLElement).closest("a");
			if (!target) return;
			const href = target.getAttribute("href");
			if (!href) return;
			const match = INTERNAL_DOC_PATTERN.exec(href);
			if (match) {
				e.preventDefault();
				const docId = match[3];
				navigate({
					to: "/doc/$docId",
					params: { docId },
					search: { highlight: undefined },
				});
			}
		},
		[navigate],
	);

	const html = useMemo(() => {
		if (chunkIndex != null && chunkIndex >= 0) {
			const chunks = chunkContent(markdown, MAX_CONTENT_CHARS);
			if (chunkIndex < chunks.length && chunks.length > 1) {
				return chunks
					.map((chunk, i) => {
						const parsed = DOMPurify.sanitize(
							marked.parse(chunk) as string,
						);
						if (i === chunkIndex) {
							return `<div class="search-chunk-highlight">${parsed}</div>`;
						}
						return parsed;
					})
					.join("");
			}
		}
		return DOMPurify.sanitize(marked.parse(markdown) as string);
	}, [markdown, chunkIndex]);

	// Scroll to chunk highlight
	useEffect(() => {
		const container = containerRef.current;
		if (!container || chunkIndex == null) return;
		const chunkEl = container.querySelector(".search-chunk-highlight");
		if (chunkEl) {
			chunkEl.scrollIntoView({ behavior: "smooth", block: "center" });
		}
	}, [html, chunkIndex]);

	// Word-level highlight (fallback when no chunkIndex)
	useEffect(() => {
		const container = containerRef.current;
		if (!container || !highlight?.trim() || chunkIndex != null) return;

		const pattern = new RegExp(`(${escapeRegExp(highlight)})`, "gi");

		// Walk text nodes and wrap matches in <mark>
		const walker = document.createTreeWalker(
			container,
			NodeFilter.SHOW_TEXT,
		);
		const textNodes: Text[] = [];
		let node: Node | null;
		while ((node = walker.nextNode())) {
			textNodes.push(node as Text);
		}

		for (const textNode of textNodes) {
			const text = textNode.textContent ?? "";
			if (!pattern.test(text)) continue;
			pattern.lastIndex = 0;

			const frag = document.createDocumentFragment();
			let lastIndex = 0;
			let match: RegExpExecArray | null;

			while ((match = pattern.exec(text)) !== null) {
				if (match.index > lastIndex) {
					frag.appendChild(
						document.createTextNode(
							text.slice(lastIndex, match.index),
						),
					);
				}
				const mark = document.createElement("mark");
				mark.className = "search-highlight";
				mark.textContent = match[0];
				frag.appendChild(mark);
				lastIndex = pattern.lastIndex;
			}

			if (lastIndex < text.length) {
				frag.appendChild(
					document.createTextNode(text.slice(lastIndex)),
				);
			}

			textNode.parentNode?.replaceChild(frag, textNode);
		}

		const firstMark = container.querySelector("mark.search-highlight");
		if (firstMark) {
			firstMark.scrollIntoView({ behavior: "smooth", block: "center" });
		}

		return () => {
			const marks = container.querySelectorAll("mark.search-highlight");
			for (const mark of marks) {
				const parent = mark.parentNode;
				if (!parent) continue;
				parent.replaceChild(
					document.createTextNode(mark.textContent ?? ""),
					mark,
				);
				parent.normalize();
			}
		};
	}, [highlight, html, chunkIndex]);

	return (
		<div
			ref={containerRef}
			className={`markdown-rendered ${className}`}
			dangerouslySetInnerHTML={{ __html: html }}
			onClick={handleClick}
		/>
	);
}
